import React, { useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { CountryService } from "../../../services/user/country.services";


const AddCountryComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const countryId = location.state?.countryId || 0;
    const isEditMode = !!countryId;

    const formik = useFormik({
        initialValues: {
            countryName: "",
            shortName: "",
            isActive: true,
        },

        validationSchema: Yup.object({
            countryName: Yup.string().required("Please enter a valid name"),
        }),

        onSubmit: async (values, { resetForm }) => {
            try {
                if (isEditMode) {
                    await CountryService.updateCountryById(countryId, values)
                        .then(response => {
                            if (response) {
                                navigate('/country');
                            }
                        });
                } else {
                    await CountryService.addNewCountry(values)
                        .then(response => {
                            if (response) {
                                navigate('/country');
                            }
                        });
                }
            } catch (error) {
                handleNotify('An error occurred while saving Country.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchCountryDetail = async () => {
                try {
                    const data = await CountryService.getCountryById(countryId);
                    formik.setValues({
                        countryId: data?.countryId,
                        countryName: data?.countryName,
                        shortName: data?.shortName,
                        isActive: data?.isActive === true ? true : false,
                    })
                }
                catch (error) {
                    handleNotify('Error fetching country details', error, TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                }
            }
            fetchCountryDetail()
        }
    }, [])

    const handleBackClick = () => {
        navigate('/country');
    };

    return (
        <>
            <div className="page-wrapper m-0 p-4">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div
                            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                            onClick={handleBackClick}
                        >
                            Countries</div>
                        <div className="ps-3">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 p-0">
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {isEditMode ? 'Update Country' : 'Add Country'}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="ms-auto">
                            <button
                                type="button"
                                className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                onClick={handleBackClick}
                            >
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="card p-4 radius-10 border-top border-0 border-4 border-primary">
                        <div className="card-body">
                            <form onSubmit={formik.handleSubmit} className="mt-4">
                                <div className="row mb-4">
                                    <div className="col-md-6 col-12">
                                        <label htmlFor="countryName" className="form-label d-flex">
                                            Name <span className="text-danger">*</span>:
                                        </label>
                                        <input
                                            type="text"
                                            id="countryName"
                                            name="countryName"
                                            className={`form-control ${formik.touched.countryName && formik.errors.countryName ? 'is-invalid' : ''}`}
                                            placeholder="Enter Country Name"
                                            value={formik.values.countryName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                        {formik.touched.countryName && formik.errors.countryName && (
                                            <div className="invalid-feedback d-flex">{formik.errors.countryName}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-4">
                                    <div className="col-md-6 col-12">
                                        <label htmlFor="shortName" className="form-label d-flex">
                                            Short Name <span className="text-danger">*</span>:
                                        </label>
                                        <input
                                            id="shortName"
                                            name="shortName"
                                            className="form-control"
                                            placeholder="Enter Short Name"
                                            value={formik.values.shortName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                    </div>
                                </div>

                                {/* Status Radio Buttons */}
                                <div className="d-flex align-items-center mb-4">
                                    <div className="form-check form-check-inline me-3">
                                        <input
                                            type="radio"
                                            id="active"
                                            name="isActive"
                                            className="form-check-input"
                                            value={true}
                                            checked={formik.values.isActive === true}
                                            onChange={() => formik.setFieldValue("isActive", true)}
                                            onBlur={formik.handleBlur}
                                        />
                                        <label className="form-check-label" htmlFor="active">
                                            Active
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            type="radio"
                                            id="inactive"
                                            name="isActive"
                                            className="form-check-input"
                                            value={false}
                                            checked={formik.values.isActive === false}
                                            onChange={() => formik.setFieldValue("isActive", false)}
                                            onBlur={formik.handleBlur}
                                        />
                                        <label className="form-check-label" htmlFor="inactive">
                                            Inactive
                                        </label>
                                    </div>
                                </div>

                                {/* Submit */}
                                <hr />
                                <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                        onClick={handleBackClick}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    >
                                        {isEditMode ? "Update" : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddCountryComponent;
